import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, CalendarDays, ClipboardList, FileText, Receipt,
  Wallet, Users, Boxes, Package, Warehouse, LogOut, PackageCheck,
  Undo2, Wrench, BarChart3, Bell, ShieldCheck, Settings, Menu, X, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: null, exact: true },
  { to: "/calendar", label: "Kalender Booking", icon: CalendarDays, roles: null },
  { to: "/bookings", label: "Booking", icon: ClipboardList, roles: null },
  { to: "/quotations", label: "Quotation", icon: FileText, roles: ["sales", "booking_admin", "finance"] },
  { to: "/invoices", label: "Invoice", icon: Receipt, roles: ["finance", "booking_admin"] },
  { to: "/payments", label: "Pembayaran", icon: Wallet, roles: ["finance"] },
  { to: "/customers", label: "Pelanggan", icon: Users, roles: null },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: null },
  { to: "/packages", label: "Paket Equipment", icon: Package, roles: ["booking_admin", "sales"] },
  { to: "/preparation", label: "Persiapan Gudang", icon: Warehouse, roles: ["warehouse"] },
  { to: "/checkout", label: "Check-Out", icon: PackageCheck, roles: ["warehouse"] },
  { to: "/return", label: "Pengembalian", icon: Undo2, roles: ["warehouse"] },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, roles: ["warehouse"] },
  { to: "/reports", label: "Laporan", icon: BarChart3, roles: ["finance", "booking_admin"] },
  { to: "/notifications", label: "Notifikasi", icon: Bell, roles: null },
  { to: "/users", label: "Pengguna & Hak Akses", icon: ShieldCheck, roles: [] }, // only super_admin
  { to: "/audit-log", label: "Audit Log", icon: History, roles: [] },
  { to: "/settings", label: "Pengaturan", icon: Settings, roles: [] },
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((i) => {
    if (i.roles === null) return true;
    if (i.roles.length === 0) return user?.role === "super_admin";
    return hasRole(...i.roles);
  });

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-white text-neutral-900" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 z-40 h-screen w-64 border-r border-neutral-200 bg-white flex flex-col transition-transform",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center gap-2 px-4 border-b border-neutral-200">
          <div className="h-7 w-7 rounded-md bg-green-600 flex items-center justify-center">
            <span className="text-white text-xs font-bold">GR</span>
          </div>
          <div className="font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Grippel RMS</div>
          <button className="lg:hidden ml-auto" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.exact}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.to.replace("/", "") || "dashboard"}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-2 text-sm transition-colors duration-150",
                  isActive
                    ? "bg-green-50 text-green-700 border-r-2 border-green-600 font-medium"
                    : "text-neutral-700 hover:bg-neutral-50",
                )
              }
            >
              <it.icon className="h-4 w-4 shrink-0" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-neutral-200 p-3">
          <div className="text-xs text-neutral-500 mb-1">Login sebagai</div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-neutral-500 mb-2 capitalize">{user?.role?.replace("_", " ")}</div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout} data-testid="btn-logout">
            <LogOut className="h-3.5 w-3.5 mr-1" /> Keluar
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sticky top-0 z-20 border-b border-neutral-200 bg-white flex items-center px-4 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5" />
          </button>
          <div className="text-sm text-neutral-600" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
            Grippel Rental Management System
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-neutral-500 hidden md:block">Jakarta · IDR · DD/MM/YYYY</div>
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
